import { Sparkles } from "lucide-react";
import { AiJobProgress } from "@/components/ai/AiJobProgress";
import { blockingMessage, isNotSetUp } from "@/components/ai/CheckWithAiButton";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useActiveAiJobs,
  useDismissFlag,
  usePhotoReview,
  useRequestPhotoReview,
} from "@/hooks/use-ai-review";
import { PHOTO_CONDITION_TAG_LABELS, PHOTO_CONDITION_TAGS } from "@/lib/enums";
import type { PhotoReviewView } from "@/schemas/ai-review";
import type { Photo } from "@/schemas/photos";

function tagLabel(tag: string): string {
  const known = PHOTO_CONDITION_TAGS.find((t) => t === tag);
  return known ? PHOTO_CONDITION_TAG_LABELS[known] : tag;
}

function Flags({ photo, review }: { photo: Photo; review: PhotoReviewView }) {
  const dismiss = useDismissFlag();
  if (!review.flags || review.flags.length === 0) return null;
  return (
    <ul data-testid="ai-flags" className="space-y-1.5">
      {review.flags.map(({ flag, label }) => (
        <li
          key={flag}
          data-testid="ai-flag"
          data-flag={flag}
          className="flex items-center justify-between gap-2"
        >
          <Badge variant="warning">{label}</Badge>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Dismiss ${label}`}
            data-testid="dismiss-flag"
            disabled={dismiss.isPending}
            onClick={() => dismiss.mutate({ photoId: photo.id, flag })}
          >
            Dismiss
          </Button>
        </li>
      ))}
    </ul>
  );
}

/** "Check this photo with AI", or its progress while the check runs. PM and admin only. */
function PhotoCheck({
  photo,
  projectId,
  reviewed,
}: {
  photo: Photo;
  projectId: string;
  reviewed: boolean;
}) {
  const jobs = useActiveAiJobs(projectId, true);
  const request = useRequestPhotoReview(projectId);
  const job = jobs.data?.active.find((j) => j.scope === "photo" && j.photoId === photo.id);
  if (job) return <AiJobProgress job={job} />;
  if (jobs.isError) {
    return (
      <ErrorState compact message="Could not load the AI checks" onRetry={() => jobs.refetch()} />
    );
  }
  const blocked = blockingMessage(request.error);
  return (
    <div className="space-y-1">
      <span title={blocked ?? undefined} className="block">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          data-testid="check-photo-with-ai"
          disabled={request.isPending || jobs.isPending || isNotSetUp(request.error)}
          onClick={() => request.mutate(photo.id)}
        >
          <Sparkles className="size-4" />
          {reviewed ? "Check again" : "Check this photo with AI"}
        </Button>
      </span>
      {blocked && <p className="text-xs text-muted-foreground">{blocked}</p>}
    </div>
  );
}

/**
 * In the viewer, under the nameplate: what the AI saw in the photo. An IE gets the description
 * and tags, or nothing at all when there is no review; a PM also gets the open flags and the
 * buttons. `projectId` is set for a PM.
 */
export function AiReviewPanel({ photo, projectId }: { photo: Photo; projectId?: string }) {
  const { data: review, isPending, isError, refetch } = usePhotoReview(photo.id);
  const canManage = projectId !== undefined;

  if (isPending) return <Skeleton className="h-20 w-full" />;
  if (isError) {
    return (
      <ErrorState
        compact
        message="Could not load the AI description"
        onRetry={() => refetch()}
        className="border-t pt-4"
      />
    );
  }
  if (!review && !canManage) return null;

  return (
    <section data-testid="ai-review-panel" className="space-y-3 border-t pt-4">
      <h3 className="text-xs text-muted-foreground">AI description</h3>
      {review ? (
        <>
          <p data-testid="ai-description" className="text-sm break-words">
            {review.description}
          </p>
          {(review.equipment || review.tags.length > 0) && (
            <div data-testid="ai-tags" className="flex flex-wrap gap-1.5">
              {review.equipment && (
                <Badge variant="secondary" className="capitalize">
                  {review.equipment}
                </Badge>
              )}
              {review.tags.map((tag) => (
                <Badge key={tag} variant="outline">
                  {tagLabel(tag)}
                </Badge>
              ))}
            </div>
          )}
          {canManage && <Flags photo={photo} review={review} />}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">Not checked by the AI yet.</p>
      )}
      {projectId && <PhotoCheck photo={photo} projectId={projectId} reviewed={Boolean(review)} />}
    </section>
  );
}
